import { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
  PaginationState,
  SortingState,
} from '@tanstack/react-table';

import {
  GroupMembership,
  InfoCircleOutline,
  MoreCircleOutline as MoreCircle,
  partitionBulkResult,
  TrashOutline as Trash,
  useToast,
} from '@aslanpay/portal-common-lib';

import UnassignPotManagerModal, {
  UnassignPotManagerAction,
} from 'app/components/Modals/UnassignPotManagerModal';
import Table from 'app/components/UI/Table/Table';
import { defaultPageSize } from 'app/constants/table';
import useGroups from 'app/hooks/useGroups';
import { GroupWithPot } from 'app/hooks/useGroupsWithPot/types';

interface PotManagersTableProps {
  companyId: string;
  groupId: string;
  flatGroupsWithPots: GroupWithPot[];
  onUnassign?: () => void;
}

interface ManagerRow {
  individualId: string;
  firstName: string;
  lastName: string;
  email: string;
  isPrimaryGroup: boolean;
}

function toManagerRow(membership: GroupMembership): ManagerRow {
  const parts = (membership.individualName ?? '').split(' ');
  return {
    individualId: membership.individualId,
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' ') || '',
    email: membership.individualEmail,
    isPrimaryGroup: membership.isPrimaryGroup,
  };
}

function buildColumns(
  managerOtherGroupsMap: Map<string, string[]>,
  groupPotNameMap: Map<string, string>,
  setPendingAction: (
    action: { manager: ManagerRow; action: UnassignPotManagerAction } | null
  ) => void
): ColumnDef<ManagerRow, unknown>[] {
  const columnHelper = createColumnHelper<ManagerRow>();

  return [
    columnHelper.accessor('firstName', {
      header: 'First name',
      enableSorting: true,
      cell: (info: CellContext<ManagerRow, unknown>) => <Text>{info.getValue() as string}</Text>,
    }) as ColumnDef<ManagerRow, unknown>,
    columnHelper.accessor('lastName', {
      header: 'Last name',
      enableSorting: true,
      cell: (info: CellContext<ManagerRow, unknown>) => <Text>{info.getValue() as string}</Text>,
    }) as ColumnDef<ManagerRow, unknown>,
    columnHelper.accessor('email', {
      header: 'Email',
      enableSorting: true,
      cell: (info: CellContext<ManagerRow, unknown>) => (
        <Text color="brand.purple">{info.getValue() as string}</Text>
      ),
    }) as ColumnDef<ManagerRow, unknown>,
    columnHelper.display({
      id: 'managesMorePots',
      header: 'Manages more pots?',
      enableSorting: false,
      cell: (info) => {
        const otherGroupIds = managerOtherGroupsMap.get(info.row.original.individualId) ?? [];
        if (otherGroupIds.length === 0) return <Text>No</Text>;

        const potNames = otherGroupIds
          .map((id) => groupPotNameMap.get(id))
          .filter((name): name is string => name !== undefined);

        return (
          <Flex align="center" gap="6px">
            <Text>Yes</Text>
            <Tooltip label={potNames.join(', ')} placement="top" hasArrow>
              <span>
                <InfoCircleOutline boxSize="18px" color="brand.purple" cursor="default" />
              </span>
            </Tooltip>
          </Flex>
        );
      },
    }),
    columnHelper.display({
      id: 'options',
      meta: {
        getCellContext: (context: CellContext<ManagerRow, unknown>) => {
          if (context.column.id === 'options') return { style: { padding: 0, textAlign: 'end' } };
          return undefined;
        },
        customStyles: { justifyContent: 'center' },
        pin: 'right',
      },
      cell: (info) => (
        <Menu autoSelect={false} strategy="fixed">
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<MoreCircle boxSize="20px" />}
            variant="menu"
          />
          <Portal>
            <MenuList>
              <MenuItem
                color="system.error"
                icon={<Trash boxSize="14px" color="system.error" />}
                onClick={() =>
                  setPendingAction({ manager: info.row.original, action: 'unassign-manager' })
                }
              >
                Unassign pot manager
              </MenuItem>
              <MenuItem
                color="system.error"
                icon={<Trash boxSize="14px" color="system.error" />}
                onClick={() =>
                  setPendingAction({ manager: info.row.original, action: 'remove-from-pot' })
                }
              >
                Unassign and remove from pot
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      ),
    }),
  ];
}

function PotManagersTable({
  companyId,
  groupId,
  flatGroupsWithPots,
  onUnassign = undefined,
}: PotManagersTableProps) {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pendingAction, setPendingAction] = useState<{
    manager: ManagerRow;
    action: UnassignPotManagerAction;
  } | null>(null);

  const { data: managersData } = useGroups.useFetchGroupMemberships(
    companyId,
    groupId,
    { membershipRole: 'POT_MANAGER' },
    !!companyId && !!groupId
  );

  const { data: allMembershipsData } = useGroups.useFetchCompanyGroupMemberships(
    companyId,
    !!companyId
  );

  const toast = useToast();
  const { mutate: updateGroupMembership, isPending: isUpdating } =
    useGroups.useUpdateGroupMemberships(companyId, groupId);
  const { mutate: removeGroupMembership, isPending: isRemoving } =
    useGroups.useRemoveGroupMemberships(companyId, groupId);

  const managerOtherGroupsMap = useMemo<Map<string, string[]>>(() => {
    const map = new Map<string, string[]>();
    const allMemberships = allMembershipsData?.list ?? [];
    allMemberships
      .filter((m) => m.membershipRole === 'POT_MANAGER' && m.groupId !== groupId)
      .forEach((m) => {
        const existing = map.get(m.individualId) ?? [];
        map.set(m.individualId, [...existing, m.groupId]);
      });
    return map;
  }, [allMembershipsData, groupId]);

  const groupPotNameMap = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    flatGroupsWithPots.forEach((g) => {
      if (g.pot) map.set(g.id, g.pot.name);
    });
    return map;
  }, [flatGroupsWithPots]);

  const currentPotName = groupPotNameMap.get(groupId) ?? '';

  const handleConfirm = () => {
    if (!pendingAction) return;
    const { manager, action } = pendingAction;
    const onSuccess = () => {
      setPendingAction(null);
      onUnassign?.();
    };

    const onUnassignError = () => {
      setPendingAction(null);
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not unassign manager',
        description: 'The manager could not be unassigned. Please try again.',
      });
    };

    if (action === 'unassign-manager') {
      updateGroupMembership(
        [
          {
            individualId: manager.individualId,
            membershipRole: 'MEMBER',
            isPrimaryGroup: manager.isPrimaryGroup,
          },
        ],
        {
          // The endpoint returns 200 even when the row fails, so inspect the per-row result.
          onSuccess: (data) => {
            const { failed } = partitionBulkResult(data);
            if (failed.length > 0) {
              setPendingAction(null);
              toast({
                position: 'top-right',
                status: 'error',
                title: 'Could not unassign manager',
                description:
                  failed[0].error?.message ??
                  'The manager could not be unassigned. Please try again.',
              });
              return;
            }
            onSuccess();
          },
          onError: onUnassignError,
        }
      );
    } else {
      removeGroupMembership([manager.individualId], { onSuccess });
    }
  };

  const allManagers = useMemo<ManagerRow[]>(() => {
    const pages = managersData?.pages ?? [];
    return pages.flatMap((p) => p.list).map(toManagerRow);
  }, [managersData]);

  const sorted = useMemo(() => {
    if (!sorting.length) return allManagers;
    const { id, desc } = sorting[0];
    return [...allManagers].sort((a, b) => {
      const aColumnValue = String((a as unknown as Record<string, unknown>)[id] ?? '');
      const bColumnValue = String((b as unknown as Record<string, unknown>)[id] ?? '');
      return desc
        ? bColumnValue.localeCompare(aColumnValue)
        : aColumnValue.localeCompare(bColumnValue);
    });
  }, [allManagers, sorting]);

  const paginated = useMemo(
    () => sorted.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [sorted, pageIndex, pageSize]
  );

  const columns = useMemo(
    () => buildColumns(managerOtherGroupsMap, groupPotNameMap, setPendingAction),
    [managerOtherGroupsMap, groupPotNameMap]
  );

  return (
    <>
      <Box minH={{ base: '300px', md: '400px' }} display="flex" flexDir="column" width="100%">
        <Table<ManagerRow>
          columns={columns}
          data={paginated}
          total={sorted.length}
          pageNumber={pageIndex}
          pageSize={pageSize}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
        />
      </Box>
      {pendingAction && (
        <UnassignPotManagerModal
          isOpen={!!pendingAction}
          onClose={() => setPendingAction(null)}
          firstName={pendingAction.manager.firstName}
          lastName={pendingAction.manager.lastName}
          potName={currentPotName}
          action={pendingAction.action}
          isLoading={isUpdating || isRemoving}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}

export default PotManagersTable;
